import React, { useEffect, useMemo, useState } from 'react';
import './ShippingCalculator.css';
import {
  FaTimes,
  FaTruck,
  FaPlane,
  FaShippingFast,
  FaBox,
  FaGlobeAmericas,
  FaCalculator,
} from 'react-icons/fa';
import { SiUps, SiFedex } from 'react-icons/si';

const API = import.meta.env.VITE_API_URL;

/* ============== Helpers & Predicates ============== */
const isUPS = (stype) => String(stype || '').toLowerCase().includes('ups');
const isFedEx = (stype) => String(stype || '').toLowerCase().includes('fedex');
const isEHub = (stype) => String(stype || '').toLowerCase().includes('ehub');
const isShipEng = (stype) => String(stype || '').toLowerCase().includes('shipengine');
const isStamps = (stype) => String(stype || '').toLowerCase().includes('stamps');

function fmtCurrency(n, code = 'USD') {
  const num = Number(n || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(
    num,
  );
}

function fmtDateYYYYMMDD(s) {
  if (!s) return '';
  const clean = String(s).replace(/-/g, '');
  if (!/^\d{8}$/.test(clean)) return s;
  const y = clean.slice(0, 4),
    m = clean.slice(4, 6),
    d = clean.slice(6, 8);
  try {
    return new Date(`${y}-${m}-${d}T00:00:00`).toLocaleDateString();
  } catch {
    return s;
  }
}

function fmtTimeHHMMSS(s) {
  if (!s) return '';
  const clean = String(s).padEnd(6, '0').slice(0, 6);
  if (!/^\d{6}$/.test(clean)) return s;
  const hh = Number(clean.slice(0, 2));
  const mm = Number(clean.slice(2, 4));
  const date = new Date();
  date.setHours(hh, mm, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function addBusinessDays(startIso, days) {
  try {
    const d = startIso ? new Date(startIso) : new Date();
    let remaining = Number(days || 0);
    if (!Number.isFinite(remaining) || remaining <= 0) return d.toLocaleDateString();
    d.setHours(0, 0, 0, 0);
    while (remaining > 0) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay(); // 0 Sun, 6 Sat
      if (day !== 0 && day !== 6) remaining -= 1;
    }
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

/* ============== Service name & icons ============== */
const UPS_NAME_MAP = {
  '01': 'UPS Next Day Air®',
  '02': 'UPS 2nd Day Air®',
  '03': 'UPS Ground',
  '07': 'UPS Worldwide Express®',
  '08': 'UPS Worldwide Expedited®',
  '11': 'UPS Standard',
  '12': 'UPS 3 Day Select®',
  '13': 'UPS Next Day Air Saver®',
  '14': 'UPS Next Day Air® Early',
  '65': 'UPS Worldwide Saver®',
  '70': 'UPS Access Point™ Economy',
};

function getUpsFriendly(code, fallbackName) {
  const c = String(code || '').toUpperCase();
  return UPS_NAME_MAP[c] || fallbackName || c;
}

function getUpsIcon(code) {
  const c = String(code || '').toUpperCase();
  if (['01', '13', '14'].includes(c))
    return <FaPlane className="svc-icon ups" title="UPS Air" />;
  if (['02', '12'].includes(c))
    return <FaShippingFast className="svc-icon ups" title="UPS 2/3 Day" />;
  if (['03'].includes(c))
    return <FaTruck className="svc-icon ups" title="UPS Ground" />;
  if (['07', '08', '11', '65', '70'].includes(c))
    return <FaGlobeAmericas className="svc-icon ups" title="UPS International" />;
  return <SiUps className="svc-icon ups" title="UPS" />;
}

const FEDEX_NAME_MAP = {
  FEDEX_GROUND: 'FedEx Ground',
  GROUND_HOME_DELIVERY: 'FedEx Home Delivery',
  FEDEX_HOME_DELIVERY: 'FedEx Home Delivery',
  FEDEX_2_DAY: 'FedEx 2Day®',
  FEDEX_2_DAY_AM: 'FedEx 2Day® A.M.',
  FEDEX_EXPRESS_SAVER: 'FedEx Express Saver®',
  PRIORITY_OVERNIGHT: 'FedEx Priority Overnight®',
  STANDARD_OVERNIGHT: 'FedEx Standard Overnight®',
  FIRST_OVERNIGHT: 'FedEx First Overnight®',
  INTERNATIONAL_PRIORITY: 'FedEx International Priority®',
  INTERNATIONAL_ECONOMY: 'FedEx International Economy®',
};

function getFedexFriendly(serviceType, fallbackName) {
  const key = String(serviceType || '').toUpperCase();
  return FEDEX_NAME_MAP[key] || fallbackName || key;
}

function getFedexIcon(serviceType) {
  const key = String(serviceType || '').toUpperCase();
  if (key.includes('OVERNIGHT'))
    return <FaPlane className="svc-icon fedex" title="FedEx Overnight" />;
  if (key.includes('2_DAY') || key.includes('SAVER') || key.includes('EXPRESS'))
    return <FaShippingFast className="svc-icon fedex" title="FedEx Express" />;
  if (key.includes('INTERNATIONAL'))
    return <FaGlobeAmericas className="svc-icon fedex" title="FedEx International" />;
  if (key.includes('GROUND') || key.includes('HOME'))
    return <FaTruck className="svc-icon fedex" title="FedEx Ground" />;
  return <SiFedex className="svc-icon fedex" title="FedEx" />;
}

/* ============== Service Cell (icon + friendly name) ============== */
function ServiceCell({ providerServiceType, serviceCode, serviceName }) {
  if (isUPS(providerServiceType)) {
    return (
      <>
        {getUpsIcon(serviceCode)} {getUpsFriendly(serviceCode, serviceName)}
      </>
    );
  }
  if (isFedEx(providerServiceType)) {
    return (
      <>
        {getFedexIcon(serviceCode)} {getFedexFriendly(serviceCode, serviceName)}
      </>
    );
  }
  if (isEHub(providerServiceType)) {
    return (
      <>
        <FaGlobeAmericas className="svc-icon ehub" title="eHub" />{' '}
        {serviceName || serviceCode || 'Service'}
      </>
    );
  }
  // ShipEngine/Stamps/unknown
  return (
    <>
      <FaBox className="svc-icon generic" /> {serviceName || serviceCode || 'Service'}
    </>
  );
}

/* ============== Component ============== */
export default function ShippingCalculator({ onClose }) {
  // Providers (UPS + FedEx + eHub + ShipEngine + Stamps) via /providers
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [appCfg, setAppCfg] = useState(null);
  // Auth state per provider
  const [authRefreshing, setAuthRefreshing] = useState(false);
  const [authError, setAuthError] = useState('');
  // Form state
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [providerId, setProviderId] = useState('');
  const [shipDate, setShipDate] = useState(todayIso);
  const [bestRate, setBestRate] = useState(false);
  // Only ZIP + country required; rest optional
  const [shipper, setShipper] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: 'US',
    residential: false,
    accountNumber: '',
  });
  const [shipTo, setShipTo] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: 'US',
    residential: true,
  });
  // Packages (user adds)
  const [packages, setPackages] = useState([]);
  // Results
  const [fetchingRates, setFetchingRates] = useState(false);
  const [rates, setRates] = useState([]);
  const [ratesError, setRatesError] = useState('');
  const [ratesWarnings, setRatesWarnings] = useState([]);

  // Load app config to prefill shipper from company address
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/appconfig`);
        const cfg = await res.json().catch(() => ({}));
        if (!alive) return;
        setAppCfg(cfg || {});
        // Prefill shipper once when fields are still empty
        setShipper((prev) => {
          const isEmpty =
            !prev.name &&
            !prev.address1 &&
            !prev.city &&
            !prev.state &&
            !prev.postalCode;
          if (!isEmpty) return prev;
          const companyName = (cfg?.companyname || 'Warehouse').toString().trim();
          const fromAddress1 = [cfg?.companystreet, cfg?.companysuite]
            .filter(Boolean)
            .join(' ')
            .trim();
          const city = cfg?.companycity || '';
          const state = cfg?.companystateabbrev || cfg?.companystate || '';
          const postal = cfg?.companyzip || '';
          const country = cfg?.companycountry || 'US';
          return {
            ...prev,
            name: companyName,
            address1: fromAddress1,
            city,
            state,
            postalCode: postal,
            countryCode: country || 'US',
            residential: false,
          };
        });
      } catch {
        if (!alive) return;
        setAppCfg({});
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load providers
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingProviders(true);
      try {
        const res = await fetch(`${API}/api/shippingintegrations/providers?enabled=1`);
        const json = await res.json();
        const rows = Array.isArray(json?.data) ? json.data : [];
        if (mounted) setProviders(rows);
      } catch {
        if (mounted) setProviders([]);
      } finally {
        if (mounted) setLoadingProviders(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedProvider = useMemo(
    () => providers.find((p) => String(p.id) === String(providerId)) || null,
    [providers, providerId],
  );

  // Refresh auth on provider change (UPS/FedEx only)
  useEffect(() => {
    let alive = true;
    (async () => {
      setAuthError('');
      setRates([]);
      setRatesError('');
      if (!selectedProvider) return;
      setAuthRefreshing(true);
      try {
        const base = selectedProvider.routeBase || '';
        const needsRefresh = base.includes('/ups') || base.includes('/fedex');
        if (!needsRefresh) {
          setAuthError('');
          return;
        }
        const id = encodeURIComponent(selectedProvider.id);
        const url = base.includes('/ups')
          ? `${API}/api/shipping/ups/${id}/auth/refresh`
          : `${API}/api/shipping/fedex/${id}/auth/refresh`;
        const res = await fetch(url, { method: 'POST' });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j?.success === false) {
          throw new Error(j?.error || 'Auth refresh failed');
        }
      } catch (e) {
        if (alive) setAuthError(e.message || 'Auth refresh failed');
      } finally {
        if (alive) setAuthRefreshing(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedProvider]);

  // Packages CRUD
  const addPackage = () => {
    setPackages((p) => [
      ...p,
      { weightUnit: 'LBS', weight: '', dimUnit: 'IN', length: '', width: '', height: '' },
    ]);
  };

  const removePackage = (i) => setPackages((p) => p.filter((_, idx) => idx !== i));

  const updatePackage = (i, patch) =>
    setPackages((p) => p.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  // Relaxed validation
  const formIsValid = useMemo(() => {
    if (!bestRate && !selectedProvider) return false;
    if (!shipper.postalCode || !shipTo.postalCode) return false;
    if (!shipper.city || !shipTo.city) return false;
    if (!packages.length) return false;
    const hasWeight = packages.some((p) => Number(p.weight) > 0);
    return hasWeight;
  }, [bestRate, selectedProvider, shipper.postalCode, shipper.city, shipTo.postalCode, shipTo.city, packages]);

  // Build a common request body (all providers accept this shape)
  const buildBody = () => ({
    shipper: {
      name: shipper.name || 'Shipper',
      accountNumber: shipper.accountNumber || undefined,
      address: {
        postalCode: shipper.postalCode,
        countryCode: shipper.countryCode || 'US',
        address1: shipper.address1 || undefined,
        address2: shipper.address2 || undefined,
        city: shipper.city || 'Unknown',
        state: shipper.state || undefined,
        residential: !!shipper.residential,
      },
    },
    shipTo: {
      name: shipTo.name || 'Recipient',
      address: {
        postalCode: shipTo.postalCode,
        countryCode: shipTo.countryCode || 'US',
        address1: shipTo.address1 || undefined,
        address2: shipTo.address2 || undefined,
        city: shipTo.city || 'Unknown',
        state: shipTo.state || undefined,
        residential: !!shipTo.residential,
      },
    },
    packages: packages.map((p) => ({
      weight: { unit: p.weightUnit || 'LBS', value: Number(p.weight || 1) },
      dimensions:
        p.length || p.width || p.height
          ? {
              unit: p.dimUnit || 'IN',
              length: Number(p.length || 1),
              width: Number(p.width || 1),
              height: Number(p.height || 1),
            }
          : undefined,
    })),
  });

  // Normalize any payload shape we might get back (server normalized or raw)
  const normalizeRates = (json, providerType, shipDate) => {
    // 1) Server-normalized
    if (Array.isArray(json?.rates)) {
      return json.rates.map((r) => {
        const onRaw =
          r.arrivesOn ||
          (r.daysInTransit != null ? addBusinessDays(shipDate, r.daysInTransit) : '');
        const on = onRaw ? fmtDateYYYYMMDD(onRaw) : '';
        const by = r.arrivesBy ? fmtTimeHHMMSS(r.arrivesBy) : '';
        return { ...r, arrivesOn: on, arrivesBy: by };
      });
    }
    // 2) eHub raw: { service_rates: [...] }
    if (Array.isArray(json?.service_rates)) {
      return json.service_rates
        .filter((r) => !r.errors)
        .map((r) => {
          let arrivesOn = '';
          let arrivesBy = '';
          if (r.delivery_date) {
            const s = String(r.delivery_date);
            if (s.includes('T')) {
              const dt = new Date(s);
              if (!Number.isNaN(dt.getTime())) {
                arrivesOn = dt.toLocaleDateString();
                arrivesBy = dt.toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                });
              }
            } else {
              arrivesOn = s; // YYYY-MM-DD
            }
          }
          return {
            serviceCode: r.service_code,
            serviceName: r.service,
            totalCharges: r.rate ?? r.meter_rate ?? r.retail_rate ?? 0,
            currency: 'USD',
            daysInTransit: r.delivery_days ?? null,
            arrivesOn,
            arrivesBy,
            guaranteed: !!r.delivery_guaranteed,
            billingWeight: null,
          };
        });
    }
    // 3) ShipEngine raw: array of rates or { rate_response: { rates: [...] } }
    const seRates = Array.isArray(json)
      ? json
      : Array.isArray(json?.rate_response?.rates)
      ? json.rate_response.rates
      : Array.isArray(json?.rates)
      ? json.rates
      : [];
    if (seRates.length) {
      return seRates.map((r) => {
        const total = Number(r?.shipping_amount?.amount ?? 0) + Number(r?.other_amount?.amount ?? 0);
        const cur = r?.shipping_amount?.currency || 'USD';
        const svc = r?.service_type || r?.service_code || 'Service';
        const svcCode = r?.service_code || svc;
        let arrivesOn = '';
        if (r?.estimated_delivery_date) {
          try {
            arrivesOn = new Date(r.estimated_delivery_date).toLocaleDateString();
          } catch {}
        }
        return {
          serviceCode: svcCode,
          serviceName: svc,
          totalCharges: total,
          currency: cur,
          daysInTransit: r?.delivery_days ?? null,
          arrivesOn,
          arrivesBy: '',
          guaranteed: !!r?.guaranteed_service,
          billingWeight: r?.carrier_weight?.value ?? null,
          carrierCode: r?.carrier_friendly_name || r?.carrier_id || undefined,
        };
      });
    }
    // Fallback: nothing recognized
    return [];
  };

  const requestRates = async () => {
    setFetchingRates(true);
    setRatesError('');
    setRates([]);
    setRatesWarnings([]);
    try {
      if (!formIsValid) {
        throw new Error(
          'Select a provider, enter shipper & ship-to ZIPs and cities, and add a package with weight.',
        );
      }
      const body = buildBody();
      if (bestRate) {
        // Pull from all available provider profiles
        const collectedWarnings = [];
        const tasks = (providers || []).map(async (p) => {
          try {
            const id = encodeURIComponent(p.id);
            const base = p.routeBase || '';
            if (!base) return [];
            let ratesUrl = `${API}${base}/${id}/rates`;
            if (isShipEng(p?.servicetype) || base.includes('/shipengine')) {
              ratesUrl = `${API}${base}/${id}/rates-estimate`;
            }
            const res = await fetch(ratesUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) return [];
            // Collect provider warnings if present
            try {
              const w = Array.isArray(json?.warnings) ? json.warnings : [];
              if (w.length) {
                const tag = p.displayname || p.servicetype || `#${p.id}`;
                w.filter(Boolean).forEach((msg) => collectedWarnings.push(`${tag}: ${String(msg)}`));
              }
            } catch {}
            const list = normalizeRates(json, p.servicetype, shipDate).map((r) => ({
              ...r,
              providerId: p.id,
              providerName: p.displayname || p.servicetype || `#${p.id}`,
              providerServiceType: p.servicetype,
            }));
            return list;
          } catch {
            return [];
          }
        });
        const results = await Promise.allSettled(tasks);
        const merged = results
          .flatMap((res) => (res.status === 'fulfilled' ? res.value : []))
          .filter((r) => r && r.totalCharges != null);
        merged.sort((a, b) => Number(a.totalCharges || 0) - Number(b.totalCharges || 0));
        setRates(merged);
        setRatesWarnings(collectedWarnings);
      } else {
        if (!selectedProvider) throw new Error('Choose a provider profile');
        const id = encodeURIComponent(selectedProvider.id);
        const base = selectedProvider.routeBase || '';
        if (!base) throw new Error('Provider routeBase not available');
        let ratesUrl = `${API}${base}/${id}/rates`;
        if (isShipEng(selectedProvider?.servicetype) || base.includes('/shipengine')) {
          ratesUrl = `${API}${base}/${id}/rates-estimate`;
        }
        const res = await fetch(ratesUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || 'Failed to fetch rates');
        // Normalize regardless of provider/backend shape
        let list = normalizeRates(json, selectedProvider.servicetype, shipDate).map((r) => ({
          ...r,
          providerId: selectedProvider.id,
          providerName: selectedProvider.displayname || selectedProvider.servicetype || `#${selectedProvider.id}`,
          providerServiceType: selectedProvider.servicetype,
        }));
        // Capture provider warnings if provided by server
        try {
          const w = Array.isArray(json?.warnings) ? json.warnings : [];
          setRatesWarnings(w.filter(Boolean).map(String));
        } catch {}
        // Sort by price ascending
        list.sort((a, b) => Number(a.totalCharges || 0) - Number(b.totalCharges || 0));
        setRates(list);
      }
    } catch (e) {
      setRatesError(e.message || 'Failed to fetch rates');
    } finally {
      setFetchingRates(false);
    }
  };

  return (
    <div className="sc-modal-overlay" onClick={onClose}>
      <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sc-head">
          <h3>
            <FaCalculator style={{ verticalAlign: '-2px', marginRight: 6 }} /> Shipping
            Calculator
          </h3>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        {/* Scrollable modal body */}
        <div className="sc-body">
          {/* Provider + Ship Date */}
          <div className="sc-row" style={{ flexWrap: 'nowrap', alignItems: 'flex-end' }}>
            {/* Provider (flexible, can shrink with icon) */}
            <label className="sc-field" style={{ minWidth: 0, flex: '1 1 0' }}>
              <span>Provider Profile</span>
              {loadingProviders ? (
                <div className="sc-inline">Loading…</div>
              ) : (
                <div className="sc-provider-select">
                  {selectedProvider?.icon && (
                    <img
                      src={selectedProvider.icon}
                      alt=""
                      className="sc-provider-icon"
                    />
                  )}
                  <select
                    value={providerId}
                    onChange={(e) => setProviderId(e.target.value)}
                    className="sc-provider-select-input"
                  >
                    <option value="">— Select —</option>
                    {providers.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.displayname || `#${p.id}`} — {p.servicetype}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </label>
            {/* Ship Date (fixed-ish width, stays next to provider) */}
            <label className="sc-field" style={{ minWidth: 140, flex: '0 0 auto' }}>
              <span>Ship Date</span>
              <input
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
              />
            </label>
            {/* Best Rate checkbox pinned to the right */}
            <label className="sc-inline" style={{ alignSelf: 'flex-end', marginLeft: 8 }}>
              <input
                type="checkbox"
                checked={bestRate}
                onChange={(e) => setBestRate(e.target.checked)}
              />
              Best Rate
            </label>
          </div>
          {!!providerId && (
            <div className="sc-inline" style={{ gap: 8, marginTop: 4, marginBottom: 4 }}>
              {authRefreshing ? (
                <span style={{ opacity: 0.8 }}>Refreshing auth…</span>
              ) : authError ? (
                <span style={{ color: '#ff8c8c' }}>{authError}</span>
              ) : (
                <span style={{ opacity: 0.7 }}>
                  {isEHub(selectedProvider?.servicetype) ||
                  isShipEng(selectedProvider?.servicetype) ||
                  isStamps(selectedProvider?.servicetype)
                    ? 'Auth OK (API-Key/Basic/SOAP)'
                    : 'Auth OK'}
                </span>
              )}
            </div>
          )}
          {/* Addresses (ZIP required; others optional) */}
          <div className="sc-grid">
            <fieldset className="sc-card">
              <legend>Shipper</legend>
              <label className="sc-field">
                <span>Name (optional)</span>
                <input
                  value={shipper.name}
                  onChange={(e) => setShipper((s) => ({ ...s, name: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>Address 1 (optional)</span>
                <input
                  value={shipper.address1}
                  onChange={(e) => setShipper((s) => ({ ...s, address1: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>Address 2 (optional)</span>
                <input
                  value={shipper.address2}
                  onChange={(e) => setShipper((s) => ({ ...s, address2: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>
                  <b>City (required)</b>
                </span>
                <input
                  value={shipper.city}
                  onChange={(e) => setShipper((s) => ({ ...s, city: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>State/Prov (optional)</span>
                <input
                  value={shipper.state}
                  onChange={(e) => setShipper((s) => ({ ...s, state: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>
                  <b>Postal Code (required)</b>
                </span>
                <input
                  value={shipper.postalCode}
                  onChange={(e) =>
                    setShipper((s) => ({ ...s, postalCode: e.target.value }))
                  }
                />
              </label>
              <label className="sc-field">
                <span>Country</span>
                <input
                  value={shipper.countryCode}
                  onChange={(e) =>
                    setShipper((s) => ({ ...s, countryCode: e.target.value }))
                  }
                />
              </label>
              <label className="sc-inline">
                <input
                  type="checkbox"
                  checked={shipper.residential}
                  onChange={(e) =>
                    setShipper((s) => ({ ...s, residential: e.target.checked }))
                  }
                />
                Residential
              </label>
              <label className="sc-field">
                <span>Account # (optional)</span>
                <input
                  value={shipper.accountNumber}
                  onChange={(e) =>
                    setShipper((s) => ({ ...s, accountNumber: e.target.value }))
                  }
                />
              </label>
            </fieldset>
            <fieldset className="sc-card">
              <legend>Ship To</legend>
              <label className="sc-field">
                <span>Name (optional)</span>
                <input
                  value={shipTo.name}
                  onChange={(e) => setShipTo((s) => ({ ...s, name: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>Address 1 (optional)</span>
                <input
                  value={shipTo.address1}
                  onChange={(e) => setShipTo((s) => ({ ...s, address1: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>Address 2 (optional)</span>
                <input
                  value={shipTo.address2}
                  onChange={(e) => setShipTo((s) => ({ ...s, address2: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>
                  <b>City (required)</b>
                </span>
                <input
                  value={shipTo.city}
                  onChange={(e) => setShipTo((s) => ({ ...s, city: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>State/Prov (optional)</span>
                <input
                  value={shipTo.state}
                  onChange={(e) => setShipTo((s) => ({ ...s, state: e.target.value }))}
                />
              </label>
              <label className="sc-field">
                <span>
                  <b>Postal Code (required)</b>
                </span>
                <input
                  value={shipTo.postalCode}
                  onChange={(e) =>
                    setShipTo((s) => ({ ...s, postalCode: e.target.value }))
                  }
                />
              </label>
              <label className="sc-field">
                <span>Country</span>
                <input
                  value={shipTo.countryCode}
                  onChange={(e) =>
                    setShipTo((s) => ({ ...s, countryCode: e.target.value }))
                  }
                />
              </label>
              <label className="sc-inline">
                <input
                  type="checkbox"
                  checked={shipTo.residential}
                  onChange={(e) =>
                    setShipTo((s) => ({ ...s, residential: e.target.checked }))
                  }
                />
                Residential
              </label>
            </fieldset>
          </div>
          {/* Packages */}
          <fieldset className="sc-card">
            <legend>Packages</legend>
            {packages.length === 0 && (
              <div style={{ marginBottom: 8, opacity: 0.85 }}>
                No packages yet — add one below. (Only <b>Weight</b> is required;
                dimensions are optional.)
              </div>
            )}
            {packages.map((p, i) => (
              <div key={i} className="sc-pack-row">
                <label className="sc-field small">
                  <span>Wgt Unit</span>
                  <select
                    value={p.weightUnit}
                    onChange={(e) => updatePackage(i, { weightUnit: e.target.value })}
                  >
                    <option value="LBS">LBS</option>
                    <option value="KGS">KGS</option>
                  </select>
                </label>
                <label className="sc-field small">
                  <span>
                    <b>Weight*</b>
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={p.weight}
                    onChange={(e) => updatePackage(i, { weight: e.target.value })}
                  />
                </label>
                <label className="sc-field small">
                  <span>Dim Unit</span>
                  <select
                    value={p.dimUnit}
                    onChange={(e) => updatePackage(i, { dimUnit: e.target.value })}
                  >
                    <option value="IN">IN</option>
                    <option value="CM">CM</option>
                  </select>
                </label>
                <label className="sc-field small">
                  <span>L</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={p.length}
                    onChange={(e) => updatePackage(i, { length: e.target.value })}
                  />
                </label>
                <label className="sc-field small">
                  <span>W</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={p.width}
                    onChange={(e) => updatePackage(i, { width: e.target.value })}
                  />
                </label>
                <label className="sc-field small">
                  <span>H</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={p.height}
                    onChange={(e) => updatePackage(i, { height: e.target.value })}
                  />
                </label>
                <button
                  className="btn danger ghost"
                  onClick={() => removePackage(i)}
                  disabled={packages.length <= 1}
                  title="Remove package"
                >
                  ✕
                </button>
              </div>
            ))}
            <div style={{ marginTop: 6 }}>
              <button className="btn" onClick={addPackage}>
                + Add Package
              </button>
            </div>
          </fieldset>
          {/* Actions */}
          <div className="sc-actions">
            <button className="btn" onClick={onClose}>
              <FaTimes style={{ marginRight: 6 }} /> Close
            </button>
            <button
              className="btn primary"
              onClick={requestRates}
              disabled={fetchingRates || !formIsValid}
            >
              {fetchingRates ? 'Getting Rates…' : (<><FaCalculator style={{ marginRight: 6 }} /> Get Rates</>)}
            </button>
          </div>
          {/* Rates */}
	          <div className="sc-results">
	            <h4>Rates</h4>
	            <div style={{ fontSize: 12, opacity: 0.85, margin: '4px 0 8px 0' }}>
	              Prices shown include carrier charges plus Partner/Juicy/3PL markups (when configured).
	            </div>
	            {!!ratesWarnings.length && !ratesError && (
	              <div className="sc-warning" style={{ marginBottom: 8, color: '#f59e0b' }}>
	                {ratesWarnings.map((w, i) => (
                  <div key={i}>• {w}</div>
                ))}
              </div>
            )}
            {ratesError ? (
              <div className="sc-error">{ratesError}</div>
            ) : fetchingRates ? (
              <div className="sc-loading">Loading…</div>
            ) : rates.length ? (
              <div className="sc-table-wrap">
                <table className="sc-table">
	                  <thead>
	                    <tr>
	                      <th className="service-col">Service</th>
	                      <th className="price-col">Price (Total)</th>
	                      <th className="date-col">Arrives On</th>
	                      <th className="time-col">Arrives By</th>
	                      <th className="tiny-col">Days</th>
	                      <th className="tiny-col">Guaranteed</th>
	                      <th className="tiny-col">Bill Wt</th>
                    </tr>
                  </thead>
                    <tbody>
                    {rates.map((r, idx) => {
                      const prov =
                        providers.find((p) => String(p.id) === String(r.providerId)) ||
                        selectedProvider ||
                        {};
	                      const vm = Number(prov.vendormarkup || 0);
	                      const jm = Number(prov.juicymarkup || 0);
	                      const m3 = Number(prov.threeplmarkup || 0); // optional; often 0 for global calculator
	                      const baseTotal = Number(r.totalCharges || 0);
	                      const vendorAmt = (Number.isFinite(baseTotal) && baseTotal > 0) ? (baseTotal * vm) : 0;
	                      const baseAfterVendor = baseTotal + vendorAmt;
	                      const juicyAmt = (Number.isFinite(baseAfterVendor) && baseAfterVendor > 0) ? (baseAfterVendor * jm) : 0;
	                      const baseAfterJuicy = baseAfterVendor + juicyAmt;
	                      const threeplAmt = (Number.isFinite(baseAfterJuicy) && baseAfterJuicy > 0) ? (baseAfterJuicy * m3) : 0;
	                      const displayTotal = baseAfterJuicy + threeplAmt;
                      try {
                        // Debug: inspect markups and totals used by ShippingCalculator
                        console.log('[ShippingCalculator][rates]', {
                          providerId: prov.id,
                          providerName: prov.displayname,
                          servicetype: prov.servicetype,
                          serviceCode: r.serviceCode,
                          baseTotal: Number(r.totalCharges || 0),
                          vendormarkup: vm,
                          juicymarkup: jm,
                          threeplmarkup: m3,
                          totalWithMarkups: displayTotal,
                        });
                      } catch {}
                      return (
                        <tr key={r.rateId || idx}>
                          <td className="service-col">
                            <ServiceCell
                              providerServiceType={r.providerServiceType || selectedProvider?.servicetype}
                              serviceCode={r.serviceCode}
                              serviceName={r.serviceName}
                            />
                            {/* NEW: tiny disambiguation line */}
                            <div style={{ fontSize: 12, opacity: .75 }}>
                              {r.providerName ? `${r.providerName} • ` : ''}
                              {r.carrierFriendly || r.carrierCode || ''} 
                              {r.packageType ? ` • ${r.packageType}` : ''} 
                              {r.rateCalcType ? ` • ${r.rateCalcType}` : ''} 
                              {r.billingWeight ? ` • Bill Wt ${r.billingWeight}` : ''} 
                              {r.zone ? ` • Zone ${r.zone}` : ''}
                            </div>
                          </td>
		                          <td className="price-col">
		                            {fmtCurrency(displayTotal, r.currency || 'USD')}
		                          </td>
                          <td className="date-col">{r.arrivesOn || '—'}</td>
                          <td className="time-col">{r.arrivesBy || '—'}</td>
                          <td className="tiny-col">{r.daysInTransit ?? '—'}</td>
                          <td className="tiny-col">{r.guaranteed ? '✔︎' : '—'}</td>
                          <td className="tiny-col">{r.billingWeight ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="sc-empty">No rates yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
